import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import {
  createForm,
  createFormArray,
  createFormControl,
  FormControls,
  SubFormArray,
  TypedAbstractControl,
  TypedFormGroup,
  WhitespaceValidator,
} from 'osn-shared';
import { MultiSelectOption } from '../../../shared/components/multi-select/multi-select.component';
import { SchedulingType } from '../../../shared/enums/scheduling-type.enum';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import { TrayFormComponentBase } from '../tray-form-component-base';
import { SearchTagsTrayItemsViewModel, SearchTagsTrayViewModel } from './search-tags-tray.view-model';
import { Validators } from '@angular/forms';
import { PageAccesses } from '../../../core/guards/page-accesses.model';
import { PageAccessesService } from '../../../core/services/page-access/page-access.service';
import { debounceTime, distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ConfirmDialogService } from '../../../shared/components/confirm-dialog/confirm-dialog.service';
import { deepEqual } from '../../../shared/functions/deepEqual';

type FormModel = Omit<SearchTagsTrayViewModel, 'id' | 'type'>;
type SimpleControls = FormControls<Omit<FormModel, 'items'>>;
type ArrayControls = SubFormArray<Pick<FormModel, 'items'>>;

export enum SearchTagsTraySubType {
  OSNPlusContentGroup = 'OSNPlusContentGroup',
  OndemandContentGroup = 'OndemandContentGroup',
}
@Component({
  selector: 'osn-cxb-search-tags-tray',
  templateUrl: './search-tags-tray.component.html',
  styleUrls: ['./search-tags-tray.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchTagsTrayComponent
  extends TrayFormComponentBase<SearchTagsTrayViewModel, FormModel, SimpleControls & ArrayControls>
  implements OnInit
{
  @Input() public isReadonly: boolean;
  @Input() public value: SearchTagsTrayViewModel;
  @Input() public countryOptions: MultiSelectOption[];
  @Input() public userSegmentOptions: MultiSelectOption[];

  private readonly FORM_DEBOUNCE_TIME = 500;
  public searchTagsTraySubTypes = [
    { value: SearchTagsTraySubType.OSNPlusContentGroup, label: 'OSN+' },
    { value: SearchTagsTraySubType.OndemandContentGroup, label: 'Ondemand' },
  ];
  protected readonly SearchTagsTraySubType = SearchTagsTraySubType;
  public accesses: PageAccesses = this.pageAccessesService.usersPageAccesses$.value;
  public readonly SchedulingType = SchedulingType;
  public subType: SearchTagsTraySubType;
  public get hasSubType() {
    return !!this.subType;
  }
  public get isSubTypeProtected(): boolean {
    return this.isReadonly || !this.accesses.screen.trayEditorScreen.trayFunctions || !!this.value.id;
  }

  constructor(
    private readonly pageAccessesService: PageAccessesService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly cdr: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit() {
    this.initForm(this.value);
    this.watchFormChange();
  }

  private watchFormChange() {
    this.form.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        tap(() => this.formValidityChange.emit(this.form.valid)),
        filter(() => this.form.enabled),
        debounceTime(this.FORM_DEBOUNCE_TIME),
        distinctUntilChanged((a, b) => deepEqual(a, b))
      )

      .subscribe((value: FormModel) => {
        const viewModelValue = this.mapFormModelToViewModel(value);
        this.valueChanges.emit(this.form.valid ? viewModelValue : undefined);
      });
  }

  protected getDefaultVm(): SearchTagsTrayViewModel {
    return {
      type: TrayFormViewModelType.SearchTagsTray,
      id: '',
      subType: '',
      titleEnglish: '',
      titleArabic: '',
      displayServiceTypeIcon: false,
      countryIds: [],
      userSegmentIds: [],
      scheduling: undefined,
      categoryListName: '',
      items: [],
    };
  }

  private getDefaultItemVm(index: number): SearchTagsTrayItemsViewModel {
    return {
      id: undefined,
      name: 'Item',
      index,
      feed: null,
      tagLocalized: undefined,
    };
  }

  protected initForm(value: SearchTagsTrayViewModel): void {
    const formValue = {
      ...this.getDefaultVm(),
      ...value,
    };
    this.subType = formValue.subType as SearchTagsTraySubType;

    this.formControls = {
      titleEnglish: createFormControl({ value: formValue.titleEnglish, disabled: this.isReadonly }, [
        Validators.required,
        WhitespaceValidator.noWhitespace,
      ]),
      titleArabic: createFormControl({ value: formValue.titleArabic, disabled: this.isReadonly }, [
        Validators.required,
        WhitespaceValidator.noWhitespace,
      ]),
      displayServiceTypeIcon: createFormControl({ value: formValue.displayServiceTypeIcon, disabled: this.isReadonly }),
      countryIds: createFormControl({ value: formValue.countryIds, disabled: this.isReadonly }, [Validators.required]),
      items: createFormArray(formValue.items.map((item) => this.createItemControl(item))),
      userSegmentIds: createFormControl({ value: formValue.userSegmentIds, disabled: this.isReadonly }, [Validators.required]),
      scheduling: createFormControl({ value: formValue.scheduling, disabled: this.isReadonly }, [Validators.required]),
      categoryListName: createFormControl({ value: formValue.categoryListName, disabled: this.isReadonly }, [Validators.required]),
      subType: createFormControl({ value: this.subType, disabled: this.isSubTypeProtected }, [Validators.required]),
    };
    this.form = createForm(this.formControls);

    if (value) {
      this.setDisabled(this.isReadonly || !this.accesses.screen.trayEditorScreen.trayFunctions);
    }
  }

  public subTypeSelectionChangeHandler({ value }) {
    const reMakeForm = () => {
      this.subType = value as SearchTagsTraySubType;
      this.form.controls.items = createFormArray([this.formControlFactory()]);
      this.form = createForm(this.formControls);
      this.watchFormChange();
      this.form.updateValueAndValidity();
    };
    if (!this.subType) {
      reMakeForm();
    } else {
      this.confirmTrayLeave$().subscribe((confirmed) => {
        if (confirmed) {
          reMakeForm();
        } else {
          this.form.controls.subType.setValue(this.subType);
        }
        this.cdr.detectChanges();
      });
    }
  }
  private confirmTrayLeave$(): Observable<boolean> {
    return this.confirmDialogService
      .openDialog({
        title: 'Leave tray?',
        description: 'Your changes will not be saved.',
        cancelBtnLabel: 'Cancel',
        confirmBtnLabel: 'Continue',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$));
  }

  public itemNameSelector = (item: SearchTagsTrayItemsViewModel, index: number): string => {
    const name = this.form?.controls?.items?.value[index]?.feed?.name ?? this.form?.controls?.items?.value[index]?.contentGroup?.name;
    return name ? `${index + 1}. ${name}` : `${index + 1}. Item`;
  };

  public formControlFactory = (): TypedAbstractControl<SearchTagsTrayItemsViewModel> => {
    return this.createItemControl(this.getDefaultItemVm(this.formControls.items.length + 1)) as any; // TODO: type compatibility?
  };

  public castItemFormGroup(formGroup: any): TypedFormGroup<SearchTagsTrayItemsViewModel> {
    return formGroup;
  }

  private createItemControl(value: SearchTagsTrayItemsViewModel): TypedFormGroup<SearchTagsTrayItemsViewModel> {
    return createForm<SearchTagsTrayItemsViewModel>({
      id: createFormControl(value.id),
      name: createFormControl(value.name),
      index: createFormControl(value.index),
      ...(this.subType === SearchTagsTraySubType.OSNPlusContentGroup
        ? { feed: createFormControl({ value: value.feed, disabled: this.isReadonly }, [Validators.required]) }
        : null),
      ...(this.subType === SearchTagsTraySubType.OndemandContentGroup
        ? { contentGroup: createFormControl({ value: value.contentGroup, disabled: this.isReadonly }, [Validators.required]) }
        : null),
      tagLocalized: createFormControl({ value: value.tagLocalized, disabled: this.isReadonly }, [Validators.required]),
    });
  }

  protected mapFormModelToViewModel(value: FormModel): SearchTagsTrayViewModel {
    return {
      ...(value as SearchTagsTrayViewModel),
      type: TrayFormViewModelType.SearchTagsTray,
      id: this.value?.id,
    };
  }
}
